import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";

interface PricingProduct {
  id: string;
  name: string;
  description?: string;
  current_price: number;
  original_price: number;
  is_limited_time: boolean;
  limited_time_text: string;
  product_type: 'course' | 'add-on' | 'subscription' | 'lifetime';
  stripe_price_id?: string;
}

const Pricing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<PricingProduct[]>([]);
  const [processingId, setProcessingId] = useState<string | null>(null);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const { data, error } = await supabase
        .from("pricing_settings")
        .select("*")
        .eq("is_active", true)
        .order("current_price", { ascending: true });

      if (error) throw error;
      setProducts((data as PricingProduct[]) || []);
    } catch (error) {
      console.error("Error loading products:", error);
      toast.error("Failed to load pricing");
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (productId: string, stripePriceId?: string) => {
    setProcessingId(productId);
    
    // Check if user is logged in
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      toast.error("Please sign in to continue");
      navigate("/auth");
      return;
    }

    // If there's a Stripe price ID, use it for checkout
    if (stripePriceId) {
      // Open Stripe checkout in new tab
      const checkoutUrl = `https://buy.stripe.com/${stripePriceId}`;
      window.open(checkoutUrl, '_blank');
      toast.success("Opening checkout...");
      setProcessingId(null);
    } else {
      // No Stripe integration, redirect to sign up
      toast.info("Redirecting to enrollment...");
      navigate("/sign-up");
      setProcessingId(null);
    }
  };

  const getProductTypeLabel = (type: string) => {
    const labels = {
      course: "Full Course",
      "add-on": "Add-On",
      subscription: "Monthly",
      lifetime: "Lifetime Access",
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getProductFeatures = (type: string) => {
    const features = {
      course: [
        "Complete course curriculum",
        "Lifetime access to materials",
        "Community access",
        "Email support",
        "Certificate of completion",
      ],
      "add-on": [
        "Additional resources",
        "Bonus content",
        "Priority support",
        "Exclusive templates",
      ],
      subscription: [
        "Monthly content updates",
        "Live Q&A sessions",
        "Community access",
        "Cancel anytime",
      ],
      lifetime: [
        "All current content",
        "All future updates",
        "Priority support",
        "Exclusive community",
        "Lifetime guarantee",
      ],
    };
    return features[type as keyof typeof features] || [];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      <main className="flex-1 pt-20">
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-7xl">
            <Button
              variant="ghost"
              onClick={() => navigate("/")}
              className="mb-8"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>

            <div className="text-center mb-16 space-y-4">
              <h1 className="text-5xl md:text-6xl font-bold">
                Choose Your <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Path</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Select the perfect plan to launch and scale your digital business
              </p>
            </div>

            {products.length === 0 ? (
              <Card className="max-w-md mx-auto">
                <CardContent className="text-center py-12">
                  <p className="text-muted-foreground">No pricing plans available at the moment.</p>
                  <Button 
                    className="mt-4"
                    onClick={() => navigate("/sign-up")}
                  >
                    Contact Us
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {products.map((product) => (
                  <Card 
                    key={product.id}
                    className="relative hover:shadow-xl transition-all duration-300 hover:scale-105"
                  >
                    {product.is_limited_time && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <Badge className="animate-pulse bg-gradient-to-r from-primary to-accent">
                          {product.limited_time_text}
                        </Badge>
                      </div>
                    )}
                    
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline">
                          {getProductTypeLabel(product.product_type)}
                        </Badge>
                      </div>
                      <CardTitle className="text-2xl">{product.name}</CardTitle>
                      {product.description && (
                        <CardDescription className="text-base">
                          {product.description}
                        </CardDescription>
                      )}
                    </CardHeader>

                    <CardContent className="space-y-6">
                      <div className="space-y-2">
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-bold">
                            ${(product.current_price / 100).toFixed(0)}
                          </span>
                          {product.original_price > product.current_price && (
                            <span className="text-xl text-muted-foreground line-through">
                              ${(product.original_price / 100).toFixed(0)}
                            </span>
                          )}
                        </div>
                        {product.original_price > product.current_price && (
                          <p className="text-sm text-green-600 font-semibold">
                            Save ${((product.original_price - product.current_price) / 100).toFixed(0)}!
                          </p>
                        )}
                      </div>

                      <div className="space-y-3">
                        {getProductFeatures(product.product_type).map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                            <span className="text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>

                    <CardFooter>
                      <Button
                        className="w-full"
                        size="lg"
                        onClick={() => handleEnroll(product.id, product.stripe_price_id)}
                        disabled={processingId === product.id}
                      >
                        {processingId === product.id ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          "Get Started"
                        )}
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}

            <div className="mt-16 text-center space-y-4">
              <p className="text-muted-foreground">
                Have questions? Need a custom plan?
              </p>
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={() => navigate("/sign-up")}
                >
                  Contact Us
                </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Pricing;
